// Firebase auth functions
import type {
  ActionCodeSettings,
  Auth,
  AuthCredential,
  AuthProvider,
  PopupRedirectResolver,
  UserCredential,
} from 'firebase/auth'

type FirebaseAuthModule = typeof import('firebase/auth')

const loadAuth = (): Promise<FirebaseAuthModule> =>
  import(/* webpackChunkName: "firebase-auth", webpackPrefetch: true */ 'firebase/auth')

export async function signInWithEmailLink(
  auth: Auth,
  email: string,
  emailLink?: string
): Promise<UserCredential> {
  const { signInWithEmailLink } = await loadAuth()
  return signInWithEmailLink(auth, email, emailLink)
}

export async function sendSignInLinkToEmail(
  auth: Auth,
  email: string,
  actionCodeSettings: ActionCodeSettings
): Promise<void> {
  const { sendSignInLinkToEmail } = await loadAuth()
  return sendSignInLinkToEmail(auth, email, actionCodeSettings)
}

export async function signInWithCredential(
  auth: Auth,
  credential: AuthCredential
): Promise<UserCredential> {
  const { signInWithCredential } = await loadAuth()
  return signInWithCredential(auth, credential)
}

export async function signInWithPopup(
  auth: Auth,
  provider: AuthProvider,
  resolver?: PopupRedirectResolver
): Promise<UserCredential> {
  const { signInWithPopup } = await loadAuth()
  return signInWithPopup(auth, provider, resolver)
}

export async function signInWithCustomToken(
  auth: Auth,
  token: string
): Promise<UserCredential> {
  const { signInWithCustomToken } = await loadAuth()
  return signInWithCustomToken(auth, token)
}

export async function getRedirectResult(
  auth: Auth,
  resolver?: PopupRedirectResolver
): Promise<UserCredential | null> {
  const { getRedirectResult } = await loadAuth()
  return getRedirectResult(auth, resolver)
}

// Browser-only resolver exposed via a lazy getter to avoid server-side imports
export async function getBrowserPopupRedirectResolver(): Promise<PopupRedirectResolver> {
  const { browserPopupRedirectResolver } = await loadAuth()
  return browserPopupRedirectResolver
}
